/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { CheckCircle2, Coins, Undo2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { api } from '../../../api';
import type { BalanceHold, HoldPurpose, HoldStatus } from '../../../api/areas/commerce';
import { Callout, DataTable, Field, FilterBar, Section, StatCard, StatGrid, type Column } from '../../../components/blocks';
import { DataError } from '../../../components/DataError';
import { GateList } from '../../../components/flow';
import { PageHeader } from '../../../components/PageHeader';
import { EmptyState, Loading } from '../../../components/States';
import { formatDateTime, formatMoney } from '../../../lib/format';
import { useApi } from '../../../lib/useApi';
import { FilterSelect, RefreshButton } from '../../registry/shared';
import { Amount } from '../../commerce/shared';
import { BackorderLinks, OVERVIEW_LIMIT } from '../module';
import { CustomerRef, HOLD_STATUS, MarketBadge, marketEnum } from '../shared';

const PURPOSES = ['DROPCATCH', 'AUCTION'] as const satisfies readonly HoldPurpose[];
type MarketPurpose = (typeof PURPOSES)[number];

/** Exact sum of non-negative decimal strings (no float rounding on money). */
export function sumDecimals(values: readonly string[]): string {
  const scale = Math.max(0, ...values.map((v) => v.split('.')[1]?.length ?? 0));
  const total = values.reduce((acc, v) => {
    const [w = '0', f = ''] = v.trim().split('.');
    return acc + BigInt(w + f.padEnd(scale, '0'));
  }, 0n);
  const s = total.toString().padStart(scale + 1, '0');
  return scale ? `${s.slice(0, -scale)}.${s.slice(-scale)}` : s;
}

/** "1.500.000 ₫ · 20 USD": one figure per currency. */
function perCurrency(rows: BalanceHold[]): string {
  if (!rows.length) return '0';
  const by = new Map<string, string[]>();
  for (const h of rows) by.set(h.currency, [...(by.get(h.currency) ?? []), h.amount]);
  return [...by.entries()].map(([currency, amounts]) => formatMoney({ amount: sumDecimals(amounts), currency })).join(' · ');
}

/** `/backorders?tab=holds`, reference 63: money held for backorders and bids, what was charged and what went back. */
export function BackorderHoldsPage() {
  const { t } = useTranslation('market');
  const [params, setParams] = useSearchParams();
  const purpose: MarketPurpose = params.get('purpose') === 'AUCTION' ? 'AUCTION' : 'DROPCATCH';
  const status = (params.get('status') ?? '') as HoldStatus | '';
  const result = useApi((signal) => api.listHolds({ purpose, status: status || undefined, limit: OVERVIEW_LIMIT }, { signal }), [purpose, status]);
  const set = (k: string, v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p);
  };

  const columns: Column<BalanceHold>[] = [
    {
      key: 'hold',
      header: t('bo.holds.for'),
      cell: (h) => (
        <div className="space-y-0.5">
          <Link to={`/holds/${encodeURIComponent(h.id)}`} className="font-semibold text-primary hover:underline">
            {h.domain_name ?? h.id.slice(0, 8)}
          </Link>
          <div className="text-xs text-muted-foreground">
            {h.resource_type === 'backorder' ? (
              <Link to={`/backorders/${encodeURIComponent(h.resource_id)}`} className="hover:underline">
                {t('bo.holds.backorder')}
              </Link>
            ) : (
              t('bo.holds.bid')
            )}
          </div>
        </div>
      ),
    },
    { key: 'customer', header: t('backorders.customer'), className: 'max-w-48 whitespace-normal', cell: (h) => <CustomerRef id={h.owner_type === 'CUSTOMER' ? h.owner_id : null} name={h.owner_name} /> },
    { key: 'amount', header: t('bo.holds.amount'), numeric: true, cell: (h) => <Amount amount={h.amount} currency={h.currency} /> },
    { key: 'status', header: t('backorders.status'), cell: (h) => <MarketBadge group="holdStatus" map={HOLD_STATUS} value={h.status} /> },
    { key: 'created', header: t('bo.holds.created'), className: 'whitespace-nowrap text-xs', cell: (h) => formatDateTime(h.created_at) },
    { key: 'closed', header: t('bo.holds.closed'), className: 'whitespace-nowrap text-xs', cell: (h) => (h.closed_at ? formatDateTime(h.closed_at) : '—') },
    {
      key: 'order',
      header: t('bo.holds.order'),
      cell: (h) =>
        h.order_id ? (
          <Link to={`/orders/${encodeURIComponent(h.order_id)}`} className="text-xs text-primary hover:underline">
            {t('bo.catching.openOrder')}
          </Link>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
  ];

  const rows = result.status === 'success' ? result.data.items : [];
  const full = result.status === 'success' && result.data.next_before !== null;
  const of = (s: HoldStatus[]) => rows.filter((h) => s.includes(h.status));

  return (
    <>
      <PageHeader title={t('bo.holds.title')} subtitle={t('bo.holds.subtitle')} actions={<RefreshButton onClick={result.reload} />} />
      <BackorderLinks here="holds" />
      <FilterBar>
        <Field label={t('bo.holds.purpose')}>
          <div role="group" aria-label={t('bo.holds.purpose')} className="inline-flex h-8 rounded-md border p-0.5">
            {PURPOSES.map((p) => (
              <button
                key={p}
                type="button"
                aria-pressed={purpose === p}
                onClick={() => set('purpose', p === 'DROPCATCH' ? '' : p)}
                className={`rounded px-3 text-[13px] font-medium ${purpose === p ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {t(`bo.holds.purposes.${p}`)}
              </button>
            ))}
          </div>
        </Field>
        <FilterSelect
          label={t('backorders.status')}
          value={status}
          onChange={(v) => set('status', v)}
          anyLabel={t('any')}
          className="min-w-40"
          options={(Object.keys(HOLD_STATUS) as HoldStatus[]).map((s) => ({ value: s, label: marketEnum('holdStatus', s) }))}
        />
      </FilterBar>
      {result.status === 'loading' ? <Loading rows={5} /> : null}
      {result.status === 'error' ? <DataError error={result.error} onRetry={result.reload} title={t('bo.holds.loadError')} /> : null}
      {result.status === 'success' ? (
        <div className="space-y-5">
          <StatGrid className="xl:grid-cols-3">
            <StatCard label={t('bo.holds.active')} icon={Coins} tone="info" value={of(['ACTIVE']).length} hint={perCurrency(of(['ACTIVE']))} />
            <StatCard label={t('bo.holds.captured')} icon={CheckCircle2} tone="ok" value={of(['CAPTURED']).length} hint={perCurrency(of(['CAPTURED']))} />
            <StatCard label={t('bo.holds.released')} icon={Undo2} value={of(['RELEASED', 'EXPIRED']).length} hint={perCurrency(of(['RELEASED', 'EXPIRED']))} />
          </StatGrid>
          {full ? <Callout tone="info">{t('bo.holds.capped', { n: OVERVIEW_LIMIT })}</Callout> : null}
          <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(0,1fr)_360px] [&>*]:min-w-0">
            <Section flush title={t(`bo.holds.purposes.${purpose}`)} description={t('bo.holds.listHint')}>
              <DataTable columns={columns} rows={rows} rowKey={(h) => h.id} empty={<EmptyState title={t('bo.holds.none')} />} />
            </Section>
            <Section title={t('bo.holds.rulesTitle')}>
              <GateList
                gates={[
                  { id: 'hold', title: t('bo.holds.rule.hold'), hint: t('bo.holds.rule.holdHint'), ok: null },
                  { id: 'capture', title: t('bo.holds.rule.capture'), hint: t('bo.holds.rule.captureHint'), ok: null },
                  { id: 'release', title: t('bo.holds.rule.release'), hint: t('bo.holds.rule.releaseHint'), ok: null },
                  { id: 'auction', title: t('bo.holds.rule.auction'), hint: t('bo.holds.rule.auctionHint'), ok: null },
                ]}
              />
            </Section>
          </div>
        </div>
      ) : null}
    </>
  );
}
